/* Copyright (c) 2021 MOCA Systems, Inc.  All rights reserved. */

import dayjs, { UnitType } from 'dayjs';
import { fancyFirebaseGetters, fancyFirebaseReferencer } from '@touchplan/fancy-referencer';
import { FancyRef } from '@touchplan/common';

const firebaseTimeOffsetPath: string = '.info/serverTimeOffset';
const fbdbTimestampPath: string = 'server_time_view/0/fbdb_current_timestamp';

/**
 * Returns a promise resolving to a timestamp in "ms since the unix epoch" based on the firebase server time.
 * Only to be used in cases where time does not need to be accurate in a range of less than 1 second
 * https://firebase.google.com/docs/database/web/offline-capabilities#clock-skew
 * Partitioning should be largely irrelevant on the premise that firebase's partitions are consistent with time
 */
export function getFirebaseTimestamp(): Promise<number> {
	// console.log('fancyfirebase time', firebase.apps.length, firebase.apps.map(t => t.name).toString());
	//do this in a slightly silly was to avoid the otherwise unavoidable Root path segment
	const offsetFancyRef = fancyFirebaseReferencer.fancyDb.getRef(firebaseTimeOffsetPath, undefined);

	return fancyFirebaseReferencer.fancyDb.get<number>(offsetFancyRef).then((offsetSnap) => {
		return new Date().getTime() + offsetSnap.val()!;
	});
}

/**
 * Does a date comparison against the firebase timestamp, returns the diff. "firebaseTimestamp - timestampMS"
 * @param timestampMs A timestamp in ms, can wait for a promise to resolve
 * @param unit Dayjs supported units, will use if provided
 */
export async function firebaseTimeDiff(
	timestampMs: number | Promise<number>,
	unit?: UnitType
): Promise<number> {
	const [firebaseNow, lastUserTimestamp] = await Promise.all([
		getFirebaseTimestamp(),
		Promise.resolve(timestampMs).then((t) => (!t ? 0 : t)), //undefineds cooerced to 0
	]);

	const firebaseNowDay = dayjs(firebaseNow);
	const lastUserTimestampDay = dayjs(lastUserTimestamp);
	return firebaseNowDay.diff(lastUserTimestampDay, unit);
}

/**
 * Sets a timestamp the firebase-y way. Doesn't _really_ do much, but you know... parity
 * @param ref A reference to where you want to write a server timestamp
 * @param magicFirebaseTimestampConst Literally firebase.database.ServerValue.TIMESTAMP
 */
export function setFirebaseTimestamp(ref: FancyRef): Promise<void> {
	return fancyFirebaseReferencer.fancyDb.set(ref, fancyFirebaseReferencer.fancyDb.ServerValue.TIMESTAMP);
}

/**
 * Gets the fbdb current timestamp in dateTimeFormat format
 * @returns fbdb timestamp
 */
export async function getFbdbTime(): Promise<string> {
	const fbdbStatsPath = fancyFirebaseGetters.getFbdbStatsPath();

	const timestampRef = fancyFirebaseReferencer.getRef(fbdbStatsPath);
	const fancyChild = fancyFirebaseReferencer.fancyDb.child(timestampRef, fbdbTimestampPath);
	const fancySnap = await fancyFirebaseReferencer.fancyDb.get<string>(fancyChild);
	return fancySnap.val()!; //practically speaking this can only be null if fbdb has never been run
}

/**
 * dayjs date, string, and time date string formats
 */
export const dateTimeFormat: string = 'YYYY-MM-DD HH:mm:ssZZ';
export const dateFormat: string = 'YYYY-MM-DD';

/**
 * Gets the fbdb current timestamp and convert to date string
 * @returns fbdb date string
 */
export async function getDateString(): Promise<string> {
	const fbdbTime = await getFbdbTime();
	return dayjs(fbdbTime).format(dateFormat);
}
